/**
 * API vérification publique — fetch direct + cache localStorage.
 *
 * Design volontairement léger : pas de RTK Query pour minimiser le bundle
 * et rester résilient hors ligne. Les fiches consultées sont mises en cache
 * localStorage (TTL 24h) pour permettre une consultation offline.
 */

const API_BASE = '/api/public/verif';
const CACHE_PREFIX = 'verif2i';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const CACHE_MAX_ENTRIES = 200;

export interface EtudiantResume {
  matricule: string;
  nom: string;
  prenoms: string;
  classe: string;
  niveau: string;
}

export interface Paiement {
  periode: string;
  libelle: string;
  statut: 'a_jour' | 'avance' | 'reliquat' | 'impaye' | 'non_importe';
  est_a_jour: boolean;
  frais_attendus: string;
  frais_payes: string;
  solde_annuel: string;
  solde_pratique: string;
  seuil_a_jour: string | null;
  saisie_manuelle: boolean;
  date_import: string | null;
  requise?: boolean;
}

export interface Placement {
  site: string;
  salle: string;
  session_mode: string;
  session_date: string;
  annee_academique: string;
}

export interface EtudiantDetails {
  matricule: string;
  nom: string;
  prenoms: string;
  sexe: string;
  date_naissance: string | null;
  lieu_naissance: string;
  niveau: string;
  option: string;
  parcours: string;
  pole: string;
  classe: string;
  type_formation: string;
  actif: boolean;
  photo_url: string | null;
}

export interface FicheEtudiant {
  found: boolean;
  matricule?: string;
  etudiant?: EtudiantDetails;
  paiements?: Paiement[];
  placement?: Placement | null;
  synthese?: {
    nb_periodes_requises: number;
    nb_a_jour: number;
    total_du_annuel: string;
    total_paye_annuel: string;
    acces_resultats: boolean;
    mode_acces: string;
    solde_total_acces: string;
  };
  _fromCache?: boolean;
  _cachedAt?: number;
}

export interface PingResponse {
  ok: boolean;
  campus: string;
  ville: string;
  deployment: string;
}

// --- Cache localStorage --------------------------------------------------

interface CachedEntry<T> {
  data: T;
  cachedAt: number;
}

function cacheKey(kind: string, ref: string): string {
  return `${CACHE_PREFIX}:${kind}:${ref.toUpperCase()}`;
}

function cacheGet<T>(key: string): CachedEntry<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEntry<T>;
    if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function cacheSet<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now() }));
    // Nettoyage LRU si on dépasse le quota
    trimCache();
  } catch {
    // Quota dépassé → on vide tout et on retente
    try {
      clearAllCache();
      localStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now() }));
    } catch { /* abandon */ }
  }
}

function trimCache(): void {
  try {
    const entries: Array<{ key: string; cachedAt: number }> = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(CACHE_PREFIX + ':')) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const p = JSON.parse(raw);
        entries.push({ key: k, cachedAt: p.cachedAt || 0 });
      } catch { /* skip */ }
    }
    if (entries.length <= CACHE_MAX_ENTRIES) return;
    entries.sort((a, b) => a.cachedAt - b.cachedAt);
    const toDrop = entries.slice(0, entries.length - CACHE_MAX_ENTRIES);
    toDrop.forEach(e => localStorage.removeItem(e.key));
  } catch { /* ignore */ }
}

export function clearAllCache(): void {
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX + ':')) toDelete.push(k);
    }
    toDelete.forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}

export function listRecentFiches(limit: number = 8): EtudiantResume[] {
  const out: Array<CachedEntry<FicheEtudiant> & { key: string }> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(`${CACHE_PREFIX}:fiche:`)) continue;
      const entry = cacheGet<FicheEtudiant>(k);
      if (entry && entry.data.found && entry.data.etudiant) {
        out.push({ ...entry, key: k });
      }
    }
    out.sort((a, b) => b.cachedAt - a.cachedAt);
    return out.slice(0, limit).map(e => ({
      matricule: e.data.etudiant!.matricule,
      nom: e.data.etudiant!.nom,
      prenoms: e.data.etudiant!.prenoms,
      classe: e.data.etudiant!.classe,
      niveau: e.data.etudiant!.niveau,
    }));
  } catch {
    return [];
  }
}

// --- Fetch ---------------------------------------------------------------

class VerifError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function fetchJson<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...opts,
    credentials: 'omit', // endpoints publics, jamais de cookie
    headers: {
      Accept: 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j.detail || msg;
    } catch { /* ignore */ }
    throw new VerifError(msg, res.status);
  }
  return res.json() as Promise<T>;
}

export async function ping(): Promise<PingResponse> {
  return fetchJson<PingResponse>(`${API_BASE}/ping/`);
}

export async function rechercher(q: string, opts?: { signal?: AbortSignal }): Promise<{
  results: EtudiantResume[];
  query: string;
  count?: number;
  detail?: string;
}> {
  const url = `${API_BASE}/recherche/?q=${encodeURIComponent(q)}`;
  return fetchJson(url, opts);
}

/**
 * Récupère une fiche par matricule.
 * Stratégie : network-first avec fallback cache. Retourne toujours le plus
 * frais disponible, mais si le réseau échoue, sert la version cachée avec
 * `_fromCache: true`.
 */
export async function getFiche(matricule: string): Promise<FicheEtudiant> {
  const key = cacheKey('fiche', matricule);
  try {
    const data = await fetchJson<FicheEtudiant>(
      `${API_BASE}/etudiant/${encodeURIComponent(matricule)}/`,
    );
    if (data.found) cacheSet(key, data);
    return data;
  } catch (err) {
    const cached = cacheGet<FicheEtudiant>(key);
    if (cached) {
      return { ...cached.data, _fromCache: true, _cachedAt: cached.cachedAt };
    }
    throw err;
  }
}

export { VerifError };
